import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Shield from 'lucide-react/dist/esm/icons/shield';
import Users from 'lucide-react/dist/esm/icons/users';
import Award from 'lucide-react/dist/esm/icons/award';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right';
import Building2 from 'lucide-react/dist/esm/icons/building-2';
import Briefcase from 'lucide-react/dist/esm/icons/briefcase';
import FileCheck from 'lucide-react/dist/esm/icons/file-check';

const HomePage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const features = [
    {
      icon: Shield,
      title: 'Proven Track Record',
      description: 'Over 20 years of excellence in financial management and advisory services with most clients achieving improved audit opinions.',
    },
    {
      icon: Award,
      title: 'Auditor-General Partnership',
      description: 'Our senior staff have been seconded to AG audits, providing insider knowledge of audit processes and expectations.',
    },
    {
      icon: Users,
      title: 'Big Firm Experience',
      description: 'Highly skilled team with extensive experience from PricewaterhouseCoopers, KPMG, and Ernst & Young.',
    },
    {
      icon: TrendingUp,
      title: 'Partner-Led Service',
      description: 'Field team members are partners in the practice, ensuring higher commitment to excellence than typical junior-led engagements.',
    },
  ];

  const services = [
    {
      icon: FileCheck,
      title: 'Audit & Assurance',
      description: 'Comprehensive statutory audits for public and private sector entities with focus on credibility and transparency.',
    },
    {
      icon: Shield,
      title: 'Internal Audit Services',
      description: 'Strengthening governance through effective internal controls, risk management, and compliance audits.',
    },
    {
      icon: Building2,
      title: 'Public Sector Solutions',
      description: 'Expert municipal and government advisory services drawing from extensive public sector experience.',
    },
    {
      icon: Briefcase,
      title: 'Business Consulting',
      description: 'Comprehensive solutions for growth, investment, and organisational excellence including venture capital consulting.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-primary to-secondary min-h-screen flex items-center">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative container-custom py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="text-white"
              initial={{ opacity: 0, x: -60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-5xl lg:text-5xl font-bold leading-snug md:leading-normal lg:leading-[1.25] mb-6">
  <span className="text-[#00A9F4]">Setting the Standard</span> in Accounting, Auditing, and Advisory Excellence
</h1>
              <p className="text-xl md:text-xl text-blue-100 mb-8 leading-relaxed">
                Leveraging over two decades of expertise to empower businesses and public institutions with expert financial guidance, regulatory compliance, and strategic growth solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
  to="/contact#contact-form"
  className="bg-[#0273D0] text-white px-8 py-4 rounded-lg font-semibold border-2 border-transparent hover:bg-[#0093D6] hover:text-white hover:border-[#0093D6] transition-colors text-center"
>
  Get in Touch
</Link>
                <Link
                  to="/services"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary transition-colors text-center"
                >
                  Learn More
                </Link>
              </div>
            </motion.div>
            <motion.div
              className="hidden lg:block"
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 border border-white border-opacity-20">
                <h3 className="text-2xl font-bold text-white mb-6">Why Choose OMC?</h3>
                <div className="space-y-4">
                  <div className="flex items-center text-white">
                    <CheckCircle className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
                    <span>20+ years of proven expertise</span>
                  </div>
                  <div className="flex items-center text-white">
                    <CheckCircle className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
                    <span>SAICA accredited professionals</span>
                  </div>
                  <div className="flex items-center text-white">
                    <CheckCircle className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
                    <span>Public sector specialists</span>
                  </div>
                  <div className="flex items-center text-white">
                    <CheckCircle className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
                    <span>Partner-led service delivery</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Professional Team Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Professional Excellence Meets Local Insight
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                At OMC, we deliver exceptional value through a framework designed for South Africa’s unique public and private sectors. Combining the precision of international accounting standards with deep local insight, we provide world-class assurance, advisory, and accounting services. Our team of seasoned professionals from leading global and local firms brings unmatched technical expertise and a practical understanding of the regulatory, cultural, and operational realities that shape success in South Africa.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img
                src="https://media.phst.co.za/file/omc/omc-home-02.png?w=600&h=500&fit=crop"
                alt="Professional team meeting"
                className="rounded-2xl shadow-xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Founder Quote Section */}
      <section className="py-20 bg-background">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div {...fadeInUp}>
              <blockquote className="text-2xl md:text-3xl font-medium text-gray-800 italic mb-6">
                "Our commitment is to elevate financial standards, turning compliance into a strategic asset for every client we serve."
              </blockquote>
              <cite className="text-lg text-primary font-semibold">
                — Oliver Chikweshe, Founder & Managing Partner, OMC Chartered Accountants
              </cite>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose OMC Chartered Accountants?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              At OMC Chartered Accountants, we understand that behind every successful business lies robust financial management and strategic insight.
            </p>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="text-center p-6 rounded-xl shadow-lg"
                variants={fadeInUp}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary text-white rounded-full mb-6">
                  <feature.icon className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="py-20 bg-background">
        <div className="container-custom">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Financial Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From audit and assurance to strategic consulting, we provide the full spectrum of professional services your organisation needs.
            </p>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow"
                variants={fadeInUp}
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-primary text-white rounded-lg">
                      <service.icon className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <Link
                      to="/services"
                      className="text-primary hover:text-secondary font-medium inline-flex items-center"
                    >
                      Learn more <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
          <motion.div className="text-center" {...fadeInUp}>
            <Link
              to="/services"
              className="bg-[#0383ED] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#1A95FF] transition-colors inline-flex items-center"
            >
              View All Services <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>

{/* Trusted By Section */}
<section className="py-20 bg-white">
  <div className="container-custom text-center mb-10">
    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
      Trusted by Leading Organisations
    </h2>
    <p className="text-gray-600 max-w-3xl mx-auto">
      We have successfully partnered with municipalities, government departments, and private sector organisations across South Africa, delivering measurable results and building lasting relationships based on trust and excellence.
    </p>
  </div>

  <div className="relative overflow-hidden group">
    {/* Scroll Container */}
    <div
      id="logoScroll"
      className="flex gap-10 animate-scroll group-hover:[animation-play-state:paused] scroll-smooth"
    >
      {[
        "https://media.phst.co.za/file/omc/omc-client-ag2.jpg",
        "https://media.phst.co.za/file/omc/omc-client-ceta.png",
        "https://media.phst.co.za/file/omc/omc-client-dohet.png",
        "https://media.phst.co.za/file/omc/omc-client-dpwr.jpg",
        "https://media.phst.co.za/file/omc/omc-client-Du-Toits.png",
        "https://media.phst.co.za/file/omc/omc-client-ehla-tvet.png",
        "https://media.phst.co.za/file/omc/omc-client-lt2.jpg",
        "https://media.phst.co.za/file/omc/omc-client-modi.png",
        "https://media.phst.co.za/file/omc/omc-client-mppt.png",
        "https://media.phst.co.za/file/omc/omc-client-musina.svg",
        "https://media.phst.co.za/file/omc/omc-client-ngaka2.jpg",
        "https://media.phst.co.za/file/omc/omc-client-nsfas.png",
        "https://media.phst.co.za/file/omc/omc-client-nw-leg.png",
        "https://media.phst.co.za/file/omc/omc-client-nw-premier.jpg",
        "https://media.phst.co.za/file/omc/omc-client-ratlou2.jpg",
        "https://media.phst.co.za/file/omc/omc-client-saed.jpg",
        "https://media.phst.co.za/file/omc/omc-ba-phala.png",
        "https://media.phst.co.za/file/omc/omc-dbsa.svg",
        "https://media.phst.co.za/file/omc/omc-ditso.png",
        "https://media.phst.co.za/file/omc/omc-doenw.jpg",
        "https://media.phst.co.za/file/omc/omc-emalahleni.jpg",
        "https://media.phst.co.za/file/omc/omc-jobsfund.png",
        "https://media.phst.co.za/file/omc/omc-kzndt.png",
        "https://media.phst.co.za/file/omc/omc-kznpt.jpg",
        "https://media.phst.co.za/file/omc/omc-labour.jpg",
        "https://media.phst.co.za/file/omc/omc-lekwa.png",
        "https://media.phst.co.za/file/omc/omc-mopani.jpg",
        "https://media.phst.co.za/file/omc/omc-msuka.png",
        "https://media.phst.co.za/file/omc/omc-nt.png",
        "https://media.phst.co.za/file/omc/omc-ramo.png",
        "https://media.phst.co.za/file/omc/omc-sasseta.png",
        "https://media.phst.co.za/file/omc/omc-transnet.png",
        "https://media.phst.co.za/file/omc/omc-tswaing2.png",
      ].map((logo, i) => (
        <div
          key={i}
          className="flex-shrink-0 flex items-center justify-center w-40 md:w-48 h-24 md:h-28 p-3 bg-white rounded-2xl shadow-sm"
        >
          <img
            src={logo}
            alt={`Client logo ${i + 1}`}
            className="h-16 md:h-20 w-auto object-contain transition-transform duration-300 hover:scale-105"
          />
        </div>
      ))}

      {/* Duplicate set for seamless looping */}
      {[
        "https://media.phst.co.za/file/omc/omc-client-ag2.jpg",
        "https://media.phst.co.za/file/omc/omc-client-ceta.png",
        "https://media.phst.co.za/file/omc/omc-client-dohet.png",
        "https://media.phst.co.za/file/omc/omc-client-dpwr.jpg",
        "https://media.phst.co.za/file/omc/omc-client-Du-Toits.png",
        "https://media.phst.co.za/file/omc/omc-client-ehla-tvet.png",
        "https://media.phst.co.za/file/omc/omc-client-lt2.jpg",
        "https://media.phst.co.za/file/omc/omc-client-modi.png",
        "https://media.phst.co.za/file/omc/omc-client-mppt.png",
        "https://media.phst.co.za/file/omc/omc-client-musina.svg",
        "https://media.phst.co.za/file/omc/omc-client-ngaka2.jpg",
        "https://media.phst.co.za/file/omc/omc-client-nsfas.png",
        "https://media.phst.co.za/file/omc/omc-client-nw-leg.png",
        "https://media.phst.co.za/file/omc/omc-client-nw-premier.jpg",
        "https://media.phst.co.za/file/omc/omc-client-ratlou2.jpg",
        "https://media.phst.co.za/file/omc/omc-client-saed.jpg",
        "https://media.phst.co.za/file/omc/omc-ba-phala.png",
        "https://media.phst.co.za/file/omc/omc-dbsa.svg",
        "https://media.phst.co.za/file/omc/omc-ditso.png",
        "https://media.phst.co.za/file/omc/omc-doenw.jpg",
        "https://media.phst.co.za/file/omc/omc-emalahleni.jpg",
        "https://media.phst.co.za/file/omc/omc-jobsfund.png",
        "https://media.phst.co.za/file/omc/omc-kzndt.png",
        "https://media.phst.co.za/file/omc/omc-kznpt.jpg",
        "https://media.phst.co.za/file/omc/omc-labour.jpg",
        "https://media.phst.co.za/file/omc/omc-lekwa.png",
        "https://media.phst.co.za/file/omc/omc-mopani.jpg",
        "https://media.phst.co.za/file/omc/omc-msuka.png",
        "https://media.phst.co.za/file/omc/omc-nt.png",
        "https://media.phst.co.za/file/omc/omc-ramo.png",
        "https://media.phst.co.za/file/omc/omc-sasseta.png",
        "https://media.phst.co.za/file/omc/omc-transnet.png",
        "https://media.phst.co.za/file/omc/omc-tswaing2.png",
      ].map((logo, i) => (
        <div
          key={`dup-${i}`}
          className="flex-shrink-0 flex items-center justify-center w-40 md:w-48 h-24 md:h-28 p-3 bg-white rounded-2xl shadow-sm"
        >
          <img
            src={logo}
            alt={`Client logo duplicate ${i + 1}`}
            className="h-16 md:h-20 w-auto object-contain transition-transform duration-300 hover:scale-105"
          />
        </div>
      ))}
    </div>
  </div>

  {/* Animation Keyframes */}
  <style jsx>{`
    @keyframes scroll {
      0% {
        transform: translateX(0);
      }
      100% {
        transform: translateX(-50%);
      }
    }
    .animate-scroll {
      display: flex;
      width: max-content;
      animation: scroll 120s linear infinite;
    }
  `}</style>
</section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Elevate Your Financial Management?
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Let us help you achieve compliance with confidence and partner with you to grow your business sustainably.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact#contact-form"
                  className="bg-[#0273D0] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#0093D6] hover:text-white hover:border-[#0093D6] transition-colors"
                >
                  Schedule a Consultation
                </Link>
                <Link
                  to="/contact#contact-form"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary transition-colors"
                >
                  Request a Proposal
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;